import React from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertTriangle, CheckCircle2, Download, X } from 'lucide-react';

export default function ExportPreview({ 
  isOpen, 
  onClose, 
  badgeData,
  onExport,
  autoCropEnabled,
  onToggleAutoCrop 
}) {
  if (!isOpen) return null;

  const overflowCount = badgeData.filter(b => b.isOverflowing).length;
  const readyCount = badgeData.filter(b => b.image && !b.isOverflowing).length;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="card-playful w-full max-w-2xl max-h-[90vh] overflow-auto bg-card border-border">
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Export Preview</h2>
              <p className="text-sm text-muted-foreground mt-1">Review your badges before exporting</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="rounded-xl"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Status Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded-2xl bg-success/10 border border-success/20">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-success" />
                <span className="font-semibold">Ready</span>
              </div>
              <p className="text-3xl font-bold text-success">{readyCount}</p>
              <p className="text-xs text-muted-foreground mt-1">Badges within safe area</p>
            </div>
            
            <div className="p-4 rounded-2xl bg-destructive/10 border border-destructive/20">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                <span className="font-semibold">Warnings</span>
              </div>
              <p className="text-3xl font-bold text-destructive">{overflowCount}</p>
              <p className="text-xs text-muted-foreground mt-1">Images outside safe area</p>
            </div>
          </div>

          {/* Auto-Crop Option */}
          {overflowCount > 0 && (
            <div className="p-4 rounded-2xl bg-primary/5 border border-primary/20">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">Auto-Crop on Export</h4>
                  <p className="text-sm text-muted-foreground">
                    Automatically crop all images to fit within cutting lines during export
                  </p>
                </div>
                <Button
                  variant={autoCropEnabled ? 'default' : 'outline'}
                  size="sm"
                  onClick={onToggleAutoCrop}
                  className="rounded-xl ml-4"
                >
                  {autoCropEnabled ? 'ON' : 'OFF'}
                </Button>
              </div>
            </div>
          )}

          {/* Preview Grid */}
          <div>
            <h3 className="font-semibold mb-3">Badge Preview</h3>
            <div className="grid grid-cols-4 gap-2 max-h-64 overflow-auto p-2 bg-muted/20 rounded-xl">
              {badgeData.map((badge, index) => (
                <div 
                  key={index}
                  className={`aspect-square rounded-lg overflow-hidden border-2 ${
                    badge.isOverflowing ? 'border-destructive' : 'border-border'
                  }`}
                >
                  {badge.image ? (
                    <img 
                      src={badge.image.url} 
                      alt={`Badge ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center text-xs text-muted-foreground">
                      {index + 1}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Warnings */}
          {overflowCount > 0 && !autoCropEnabled && (
            <div className="p-4 rounded-2xl bg-destructive/10 border border-destructive/50 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-destructive mb-1">Warning: Content Outside Safe Area</p>
                <p className="text-sm text-muted-foreground">
                  {overflowCount} badge{overflowCount > 1 ? 's' : ''} have content extending beyond cutting lines. 
                  Enable auto-crop or adjust manually before exporting.
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={onExport}
              className="flex-1 rounded-xl bg-gradient-to-r from-primary to-secondary hover:shadow-hover"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
